import type { SessionState } from '../core/types';

/**
 * Session visualizer (spec §6.2).
 *
 * Concentric rings pulse at the ACTUAL gate frequency reported by the device,
 * not a decorative rate — the animation period is 1/freq seconds. Three rings
 * staggered by a third of a period each give a continuous outward flow.
 *
 * Above ~8 Hz individual pulses stop being distinguishable, so the rate is
 * displayed numerically and the animation is capped: a 200 Hz ring would just
 * be a flicker, and flicker in the 13-30 Hz band is exactly what we tell
 * people to avoid elsewhere in this app. Capping it is deliberate.
 */

const RING_COUNT = 3;
const MAX_VISUAL_HZ = 8;

export function Visualizer({ session }: { session: SessionState }) {
  const idle = !session.running || session.phase === 'IDLE';
  const resting = session.phase === 'REST';

  const hz = session.freq > 0 ? session.freq : 0;
  const visualHz = Math.min(Math.max(hz, 0.5), MAX_VISUAL_HZ);
  const period = 1 / visualHz;

  const mode = idle ? 'idle' : resting ? 'resting' : 'treating';

  return (
    <div className={`viz ${mode}`}>
      {!idle
        ? Array.from({ length: RING_COUNT }, (_, i) => (
            <span
              key={i}
              className="viz-ring"
              style={{
                animationDuration: `${period.toFixed(3)}s`,
                animationDelay: `${((period / RING_COUNT) * i).toFixed(3)}s`,
              }}
            />
          ))
        : null}

      <div className="viz-core">
        <div className="viz-hz">{hz > 0 ? hz : '—'}</div>
        <div className="viz-unit">{hz > 0 ? 'Hz gate' : 'idle'}</div>
      </div>
    </div>
  );
}

export function StatRow({ session }: { session: SessionState }) {
  const phaseLabel = !session.running ? 'IDLE' : session.phase === 'REST' ? 'RESTING' : 'TREATING';
  const tone = !session.running ? 'neutral' : session.phase === 'REST' ? 'warn' : 'good';

  return (
    <div className="stat-row">
      <div className="stat">
        <div className="stat-value">
          {session.totalCycles > 0 ? `${session.cycle}/${session.totalCycles}` : '—'}
        </div>
        <div className="stat-label">Cycle</div>
      </div>
      <div className="stat">
        <div className="stat-value">{session.timeLeft > 0 ? session.timeLeft : '—'}</div>
        <div className="stat-label">Min left</div>
      </div>
      <div className="stat">
        <div className="stat-value">
          <span className={`badge ${tone}`}>{phaseLabel}</span>
        </div>
        <div className="stat-label">Phase</div>
      </div>
    </div>
  );
}
